import { motion, useScroll, useTransform } from 'framer-motion'
import { useRef } from 'react'
import { Link } from 'react-router-dom'
import { ArrowRight } from 'lucide-react'

const IMAGE_URL = 'https://images.pexels.com/photos/32977153/pexels-photo-32977153.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940'

export default function AboutTeaser() {
  const ref = useRef(null)
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] })
  const y = useTransform(scrollYProgress, [0, 1], ['0%', '15%'])

  return (
    <section
      ref={ref}
      data-testid="about-teaser-section"
      id="about-teaser"
      className="py-32 md:py-40 px-6 md:px-12 lg:px-24 bg-white overflow-hidden"
    >
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          {/* Left: text */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.85, ease: [0.16, 1, 0.3, 1] }}
          >
            <p className="text-[10px] tracking-[0.35em] uppercase text-[#0A0A0A]/30 mb-6 font-body font-semibold">
              About Strive Partners
            </p>
            <h2 className="text-[clamp(2.5rem,5vw,5rem)] font-heading font-black tracking-tighter leading-none text-[#0A0A0A] mb-8">
              Search built around<br />
              <span className="font-light text-[#16365F]">business outcomes.</span>
            </h2>
            <p className="text-sm md:text-base font-body text-[#0A0A0A]/55 leading-relaxed mb-6 max-w-md">
              Strive Partners was founded by professionals who have led talent acquisition inside some of the most demanding technology organisations globally. We understand both sides of the table.
            </p>
            <p className="text-sm font-body text-[#0A0A0A]/55 leading-relaxed mb-10 max-w-md">
              Our search work is grounded in the same rigour we applied when building and scaling teams — assessing candidates, shaping hiring briefs, and being held accountable for outcomes.
            </p>
            <Link
              to="/contact"
              data-testid="about-teaser-cta"
              className="inline-flex items-center gap-3 text-[10px] font-body font-semibold tracking-[0.18em] uppercase text-[#0A0A0A] border-b border-[#0A0A0A]/30 pb-0.5 hover:text-[#0B1F3A] hover:border-[#0B1F3A] transition-colors"
            >
              Start a conversation <ArrowRight size={11} />
            </Link>
          </motion.div>

          {/* Right: parallax image */}
          <div className="relative h-[400px] md:h-[520px] overflow-hidden bg-[#F6F7F9]">
            <motion.img
              src={IMAGE_URL}
              alt="Architecture — editorial"
              style={{ y }}
              className="w-full h-[120%] object-cover object-center grayscale"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-[#0B1F3A]/20" />
          </div>
        </div>
      </div>
    </section>
  )
}
